// hooks/useMarketPlace.js - نسخه فیکس شده
import { useState, useEffect, useCallback } from 'react';
import { notNullAndUndefined } from '../../../helper';
import { product_list_localstorage, conf } from '../../../confg';
import { calculateTotals, getInitialTotals, getCartItems } from '../utils/cartCalculations';
import { transformLocalStorageProducts } from '../utils/productTransformer';
import { getDefaultAddress } from '../utils/apiService';

const useMarketPlace = ({ user, cart, cart_report, cart_update, cart_credit, location, set_me, getBasket, remove_product_basket, translate }) => {
  // State Management
  const [products, setProducts] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [currentView, setCurrentView] = useState('main');
  const [deleteThisProductId, setDeleteThisProductId] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(!!location?.state?.showModal);
  const [totals, setTotals] = useState(getInitialTotals());
  const [totalForNotLogginUser, setTotalForNotLogginUser] = useState(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [goToStore, setGoToStore] = useState(false);
  const [redirectToShippingPage, setRedirectToShippingPage] = useState(false);

  // اضافه کردن state برای force update کردن displayProducts
  const [forceUpdate, setForceUpdate] = useState(0);

  // ساده‌تر: فقط یک state برای تشخیص اینکه آیا initialized شده یا نه
  const [isInitialized, setIsInitialized] = useState(false);

  // تشخیص نوع کاربر
  const isLoggedIn = notNullAndUndefined(localStorage.getItem('token'));
  const isGuestUser = !isLoggedIn;

  // Get address default
  const getAddressDefault = useCallback(async () => {
    if (!user.id) return;

    try {
      const address = await getDefaultAddress(user.id);
      setDefaultAddress(address);
    } catch (error) {
      setDefaultAddress(null);
    }
  }, [user.id]);

  // Load localStorage products
  const loadLocalStorageProducts = useCallback(() => {
    const localProducts = JSON.parse(localStorage.getItem(product_list_localstorage)) || [];

    setProducts(localProducts);
    setShowLoader(false);
    setIsInitialized(true);

    // محاسبه قیمت کل برای localStorage
    if (localProducts.length > 0) {
      const total = localProducts.reduce((sum, product) => {
        return sum + parseFloat(product.price || 0);
      }, 0);
      setTotals((prev) => ({ ...prev, totalPrice: parseFloat(total / 10).toFixed(0) }));
      setTotalForNotLogginUser(parseFloat(total / 10).toFixed(0));
    } else {
      // اگر محصولی نداریم، total رو صفر کن
      setTotals((prev) => ({ ...prev, totalPrice: 0 }));
      setTotalForNotLogginUser(0);
    }

    return localProducts;
  }, []);

  // Get display products - اضافه کردن forceUpdate به dependencies
  const getDisplayProducts = useCallback(() => {
    if (!isInitialized) {
      return [];
    }

    if (isLoggedIn && Object.keys(user).length > 0) {
      // کاربر لاگین کرده
      const serverProducts = cart.products || [];
      return serverProducts;
    } else if (isGuestUser || Object.keys(user).length == 0) {
      // کاربر guest
      const productStorage = localStorage.getItem(product_list_localstorage);
      if (productStorage && notNullAndUndefined(productStorage)) {
        const products_list = JSON.parse(productStorage);
        const transformed = transformLocalStorageProducts(products_list);
        return transformed;
      }
      return [];
    }

    return [];
  }, [user, cart.products, isInitialized, isLoggedIn, isGuestUser, forceUpdate]); // اضافه کردن forceUpdate

  // Delete from cart - فیکس شده
  const deleteFromCart = useCallback(
    (productId) => {
      if (!productId) {
        console.error('❌ No productId provided');
        return;
      }

      setDeleteThisProductId(productId);

      if (isGuestUser || Object.keys(user).length == 0) {
        // برای guest user، فوری از localStorage حذف کن
        try {
          const localProducts = JSON.parse(localStorage.getItem(product_list_localstorage)) || [];
          const filteredProducts = localProducts.filter((p) => p?.product?.id !== productId && p?.id !== productId);

          localStorage.setItem(product_list_localstorage, JSON.stringify(filteredProducts));
          setProducts(filteredProducts); // آپدیت کردن products state

          // محاسبه مجدد قیمت کل
          if (filteredProducts.length > 0) {
            const total = filteredProducts.reduce((sum, product) => {
              return sum + parseFloat(product.price || 0);
            }, 0);
            setTotals((prev) => ({ ...prev, totalPrice: parseFloat(total / 10).toFixed(0) }));
            setTotalForNotLogginUser(parseFloat(total / 10).toFixed(0));
          } else {
            // اگر محصولی نمونده، total رو صفر کن
            setTotals((prev) => ({ ...prev, totalPrice: 0 }));
            setTotalForNotLogginUser(0);
          }

          // Force update کردن displayProducts
          setForceUpdate((prev) => prev + 1);

          // Reset delete state
          setTimeout(() => {
            setDeleteThisProductId(null);
          }, 500);
        } catch (error) {
          console.error('❌ Error deleting from localStorage:', error);
          setDeleteThisProductId(null);
        }
      } else {
        // برای logged user، از API استفاده کن
        try {
          remove_product_basket(productId);
        } catch (error) {
          console.error('❌ Error calling delete API:', error);
          setDeleteThisProductId(null);
        }
      }
    },
    [isGuestUser, remove_product_basket, user],
  );

  // Handle next step
  const handleNext = useCallback(() => {
    // if (user.emta_validate === 0) {
    //   window.location.replace(`${conf.server_url}emta?jwt=${localStorage.getItem('token')}`);
    // } else {
    setRedirectToShippingPage(true);
    // }
  }, [user.emta_validate]);

  // Handle back to store
  const handleBackToStore = useCallback(() => {
    setGoToStore(true);
  }, []);

  // Effect اولیه - تشخیص نوع کاربر و بارگذاری اولیه
  useEffect(() => {
    if (isGuestUser || Object.keys(user).length == 0) {
      // اگر guest user هست، فوری localStorage رو بخون
      loadLocalStorageProducts();
    } else {
      // اگر logged user هست، user info رو بگیر
      setShowLoader(true);
      set_me();
    }
  }, []); // فقط یکبار اجرا بشه

  // Effect برای logged users - وقتی user data آماده شد
  useEffect(() => {
    if (isLoggedIn && Object.keys(user).length > 0 && user.id) {
      // آدرس پیش‌فرض رو بگیر
      getAddressDefault();

      // سبد خرید رو بگیر
      getBasket();
    }
  }, [user.id, isLoggedIn, getAddressDefault, getBasket]);

  // Effect برای cart_update - وقتی سبد خرید آپدیت شد
  useEffect(() => {
    if (cart_update && isLoggedIn) {
      const serverProducts = cart.products || [];
      setProducts(serverProducts);
      setShowLoader(false);
      setIsInitialized(true);

      // Reset delete state
      if (deleteThisProductId) {
        setTimeout(() => {
          setDeleteThisProductId(null);
        }, 1000);
      }
    }
  }, [cart_update, cart.products, isLoggedIn, deleteThisProductId]);

  // Effect برای cart_report
  useEffect(() => {
    if (Object.keys(cart_report).length > 0 || Object.keys(user).length > 0) {
      setTotals(calculateTotals(cart_report));
    }
  }, [cart_report]);

  // Safety timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showLoader && !isInitialized) {
        if (isGuestUser || Object.keys(user).length == 0) {
          loadLocalStorageProducts();
        } else {
          setShowLoader(false);
          setIsInitialized(true);
        }
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [showLoader, isInitialized, isGuestUser, loadLocalStorageProducts]);

  const displayProducts = getDisplayProducts();
  const cart_items = getCartItems(totals, user, translate);

  return {
    displayProducts,
    showLoader,
    currentView,
    setCurrentView,
    showErrorModal,
    setShowErrorModal,
    deleteFromCart,
    deleteThisProductId,
    totals,
    totalForNotLogginUser,
    cart_items,
    goToStore,
    redirectToShippingPage,
    defaultAddress,
    handleNext,
    handleBackToStore,
  };
};

export default useMarketPlace;
